import { Controller } from '@hotwired/stimulus'
import { Sortable } from './mixins/sortable'

export default class extends Sortable(Controller) {
  static targets = ['container', 'template', 'row', 'sortableItem', 'card']

  add (event) {
    event.preventDefault()
    const type = (event.params && event.params.type) || 'short_text'

    const template = this.templateTargets.find(t => t.dataset.type === type) || this.templateTarget
    if (!template) return

    const html = template.innerHTML.replace(/NEW_RECORD/g, this.uniqueId())
    const wrapper = document.createElement('div')
    wrapper.innerHTML = html.trim()
    const row = wrapper.firstElementChild

    this.containerTarget.appendChild(row)
    this.collapseOthers(row.querySelector('details'))
    row.classList.add('question-enter')
    row.addEventListener('animationend', () => row.classList.remove('question-enter'), { once: true })
    row.querySelector("[data-field-row-target='label']")?.focus({ preventScroll: true })
    row.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  toggleRow (event) {
    const card = event.target
    this.refreshSummary(card)
    if (card.open) this.collapseOthers(card)
  }

  rowSaving () {
    this.scrollBeforeSave = window.scrollY
  }

  rowSaved () {
    if (this.scrollBeforeSave == null) return

    const top = this.scrollBeforeSave
    this.scrollBeforeSave = null
    requestAnimationFrame(() => {
      if (window.scrollY !== top) window.scrollTo(0, top)
    })
  }

  remove (event) {
    event.preventDefault()
    const row = event.target.closest("[data-form-fields-target~='row']")
    if (!row) return
    row.remove()
  }

  grab (event) {
    const row = event.currentTarget.closest("[data-form-fields-target~='sortableItem']")
    if (row) row.setAttribute('draggable', 'true')
  }

  release (event) {
    const row = event.currentTarget.closest("[data-form-fields-target~='sortableItem']")
    if (row) row.removeAttribute('draggable')
  }

  dragImageFor (row) {
    return row.querySelector('details')
  }

  onDragEnd (moved) {
    this.sortableItemTargets.forEach((row) => row.removeAttribute('draggable'))
    this.saveOrder(moved)
  }

  saveOrder (row) {
    if (!row || !row.dataset.fieldId) return

    const positionField = row.querySelector("[data-field-row-target='position']")
    if (!positionField) return

    const index = this.visibleRows.filter((r) => r.dataset.fieldId).indexOf(row)
    if (index < 0) return

    positionField.value = index + 1
    positionField.form.requestSubmit()
  }

  collapseOthers (openCard) {
    this.cardTargets.forEach((card) => {
      if (card !== openCard) card.open = false
    })
  }

  markRequired (event) {
    const card = event.target.closest("[data-form-fields-target~='card']")
    card?.querySelectorAll('[data-question-required]')
      .forEach((mark) => mark.classList.toggle('hidden', !event.target.checked))
  }

  refreshSummary (card) {
    const label = card.querySelector("[data-field-row-target='label']")
    const summary = card.querySelector('[data-question-summary]')
    if (summary) summary.textContent = label?.value.trim() || 'Untitled question'

    const detail = card.querySelector('[data-question-detail]')
    if (detail) detail.textContent = this.detailFor(card)

    const required = card.querySelector('input[type="checkbox"][name*="[required]"]')?.checked
    card.querySelectorAll('[data-question-required]')
      .forEach((mark) => mark.classList.toggle('hidden', !required))
  }

  detailFor (card) {
    const instructions = card.querySelector('[data-field-row-target="instructions"] input, [data-field-row-target="instructions"] textarea')
    if (instructions?.value.trim()) return instructions.value.trim()

    const options = Array.from(card.querySelectorAll('[data-options-list-target="row"] input'))
      .map((input) => input.value.trim())
      .filter(Boolean)
    if (options.length) return options.join(', ')

    return card.dataset.questionType
  }

  get visibleRows () {
    return Array.from(this.containerTarget.querySelectorAll("[data-form-fields-target~='row']"))
      .filter(row => !row.classList.contains('hidden'))
  }

  uniqueId () {
    this.rowSequence = (this.rowSequence || 0) + 1
    return `${Date.now()}-${this.rowSequence}`
  }
}
